/**
 * useUpdateSteward Hook
 *
 * React Query mutation hook for updating steward status (active/inactive toggle).
 * Provides optimistic updates and automatic cache invalidation.
 *
 * Usage:
 * ```tsx
 * const updateMutation = useUpdateSteward('stewards');
 *
 * updateMutation.mutate({
 *   row: steward,
 *   nextActive: true
 * });
 * ```
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSteward } from '../api/api';

interface Steward {
  id: number | string;
  name: string;
  email?: string;
  stewardCode?: string;
  active: boolean;
  assignedCount: number;
  lastLogin?: string | null;
  createdAt?: string;
  activeRequests?: number;
}

interface UpdateStewardParams {
  row: Steward;
  nextActive: boolean;
}

/**
 * Hook for updating steward active status
 *
 * @param queryKey - Query key for cache invalidation (e.g., 'stewards', ['stewards', filters])
 * @returns React Query mutation object
 */
export function useUpdateSteward(queryKey: string | any[]) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ row, nextActive }: UpdateStewardParams) => {
      // Call the update API with minimal required fields
      const stewardId = Number(row.id);
      const updateData = {
        name: row.name,
        email: row.email || '',
        stewardCode: row.stewardCode || '',
        isActive: nextActive,
      };

      return await updateSteward(stewardId, updateData);
    },

    // Optimistic update: immediately update the UI before the API call completes
    onMutate: async ({ row, nextActive }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      const normalizedKey = Array.isArray(queryKey) ? queryKey : [queryKey];
      await queryClient.cancelQueries({ queryKey: normalizedKey });

      // Snapshot the previous value
      const previousStewards = queryClient.getQueryData(normalizedKey);

      // Optimistically update the cache
      queryClient.setQueryData(normalizedKey, (old: Steward[] | undefined) => {
        if (!old) return old;

        return old.map((steward) =>
          steward.id === row.id
            ? { ...steward, active: nextActive }
            : steward
        );
      });

      // Return context with previous value for rollback
      return { previousStewards };
    },

    // On error, roll back to the previous value
    onError: (err, variables, context) => {
      if (context?.previousStewards) {
        const normalizedKey = Array.isArray(queryKey) ? queryKey : [queryKey];
        queryClient.setQueryData(normalizedKey, context.previousStewards);
      }
      console.error('Error updating steward:', err);
    },

    // Always refetch after error or success to ensure consistency
    onSettled: () => {
      const normalizedKey = Array.isArray(queryKey) ? queryKey : [queryKey];
      queryClient.invalidateQueries({ queryKey: normalizedKey });
    },
  });
}
